import React from 'react';

import {type Post} from 'mattermost-redux/types/posts';
import {type Reaction as ReactionType} from 'mattermost-redux/types/reactions';

import OverlayTrigger from 'components/overlay_trigger';
import Tooltip from 'components/tooltip';

import Constants, {ModalIdentifiers} from 'utils/constants';
import * as Utils from 'utils/utils';

import './reaction.scss';
import {type ModalData} from 'types/actions';
import {ReactionsModal} from 'components/reactions_modal';

import ReactionTooltip from './reaction_tooltip';

type State = {
    displayNumber: number;
    reactedClass: 'Reaction--reacted' | 'Reaction--reacting' | 'Reaction--unreacted' | 'Reaction--unreacting';
};

declare module 'react-bootstrap/lib/OverlayTrigger' {
    interface OverlayTriggerProps {
        shouldUpdatePosition?: boolean;
    }
}

type ReactionChangeCb = (postId: string, emojiName: string) => Promise<{data?: boolean}>;

type Props = {

    /*
     * The post to render the reaction for
     */
    post: Post;

    /*
     * The user id of the logged in user
     */
    currentUserId: string;

    /*
     * Is should be reactions modal
     */
    hasReactionsModal: boolean;

    /*
     * The name of the emoji for the reaction
     */
    emojiName: string;

    /*
     * The number of reactions to this post for this emoji
     */
    reactionCount: number;

    /*
     * Array of reactions by user
     */
    reactions: ReactionType[];

    /*
     * True if the user has the permission to add a reaction in this channel
     */
    canAddReactions: boolean;

    /*
     * True if user has the permission to remove his own reactions in this channel
     */
    canRemoveReactions: boolean;

    /*
     * The URL of the emoji image
     */
    emojiImageUrl?: string;

    /*
     * Whether or not the current user has used this reaction
     */
    currentUserReacted: boolean;

    actions: {

        /*
         * Function to add a reaction to a post
         */
        addReaction: ReactionChangeCb;

        /*
         * Function to add a reaction to a post
         */
        addReactionToPost: ReactionChangeCb;

        /*
         * Function to open reactions modal
         */
        openModal: <P>(modalData: ModalData<P>) => void;

        /*
         * Function to get non-loaded profiles by id
         */
        fetchUsersByIdsDebounced: (ids: string[]) => void;

        /*
         * Function to remove a reaction from a post
         */
        removeReaction: ReactionChangeCb;

    };

    /**
     * @default true
     */
    addToRecent?: boolean;
};

export default class Reaction extends React.PureComponent<Props, State> {
    private reactionButtonRef = React.createRef<HTMLButtonElement>();
    private reactionCountRef = React.createRef<HTMLSpanElement>();
    private inProgress = false;

    constructor(props: Props) {
        super(props);

        const {currentUserReacted, reactionCount} = this.props;

        if (currentUserReacted) {
            this.state = {
                reactedClass: 'Reaction--reacted',
                displayNumber: reactionCount,
            };
        } else {
            this.state = {
                reactedClass: 'Reaction--unreacted',
                displayNumber: reactionCount,
            };
        }
    }

    componentDidUpdate(prevProps: Props): void {
        if (prevProps.reactionCount !== this.props.reactionCount) {
            const {currentUserReacted} = this.props;
            const reactedClass = currentUserReacted ? 'Reaction--reacted' : 'Reaction--unreacted';

            this.inProgress = false;
            /* eslint-disable-next-line react/no-did-update-set-state */
            this.setState({
                displayNumber: this.props.reactionCount,
                reactedClass,
            });
        }
    }

    handleContextMenu = (e: React.SyntheticEvent) => {
        if (!this.props.hasReactionsModal) {
            return;
        }
        e.preventDefault();
        const reactionsModalData = {
            modalId: ModalIdentifiers.MESSAGE_REACTIONS,
            dialogType: ReactionsModal,
            dialogProps: {
                postId: this.props.post.id,
                defaultSelected: this.props.emojiName,
            },
        };

        this.props.actions.openModal(reactionsModalData);
    };

    handleClick = (): void => {
        // only proceed if user has permission to react and we are not in progress of reacting
        if (!(this.props.canAddReactions && this.props.canRemoveReactions) || this.inProgress) {
            return;
        }

        const {currentUserReacted} = this.props;

        this.inProgress = true;
        this.setState((state) => {
            if (currentUserReacted) {
                return {
                    displayNumber: state.displayNumber - 1,
                    reactedClass: 'Reaction--unreacting',
                };
            }

            return {
                displayNumber: state.displayNumber + 1,
                reactedClass: 'Reaction--reacting',
            };
        });
    };

    handleAnimationEnded = (): void => {
        const {actions, currentUserReacted, post, emojiName, addToRecent = true} = this.props;

        this.setState<'reactedClass'>((state) => {
            if (state.reactedClass === 'Reaction--reacting') {
                return {
                    reactedClass: 'Reaction--reacted',
                };
            } else if (state.reactedClass === 'Reaction--unreacting') {
                return {
                    reactedClass: 'Reaction--unreacted',
                };
            }
            return state;
        });

        let action: ReactionChangeCb;

        if (currentUserReacted) {
            action = actions.removeReaction;
        } else if (addToRecent) {
            action = actions.addReaction;
        } else {
            action = actions.addReactionToPost;
        }

        action(post.id, emojiName)
            .then((res) => {
                // Если сервер вернул ошибку, то восстанавливаем предыдущее состояние
                if (!res?.data) {
                    this.setState((state) => ({
                        displayNumber: this.props.reactionCount,
                        reactedClass: ['Reaction--reacted', 'Reaction--reacting'].includes(state.reactedClass) ? 'Reaction--unreacted' : 'Reaction--reacted',
                    }));
                }
            })
            .finally(() => {
                this.inProgress = false;
            });
    };

    loadMissingProfiles = async (): Promise<void> => {
        const ids = this.props.reactions.map((reaction) => reaction.user_id);
        this.props.actions.fetchUsersByIdsDebounced(ids);
    };

    render(): React.ReactNode {
        if (!this.props.emojiImageUrl) {
            return null;
        }
        const {canAddReactions, canRemoveReactions, currentUserReacted, emojiName, reactionCount, reactions} =
            this.props;
        const {displayNumber} = this.state;
        const reactedNumber = currentUserReacted ? reactionCount : reactionCount + 1;
        const unreactedNumber = currentUserReacted ? reactionCount - 1 : reactionCount;
        const unreacted = unreactedNumber > 0 ? unreactedNumber : '';
        const reacted = reactedNumber > 0 ? reactedNumber : '';
        const display = displayNumber > 0 ? displayNumber : '';
        const readOnlyClass = canAddReactions && canRemoveReactions ? '' : 'Reaction--read-only';

        const emojiNameWithSpaces = this.props.emojiName.replace(/_/g, ' ');
        let ariaLabelEmoji = `${Utils.localizeMessage(
            'reaction.reactWidth.ariaLabel',
            'react with',
        )} ${emojiNameWithSpaces}`;
        if (currentUserReacted && canRemoveReactions) {
            ariaLabelEmoji = `${Utils.localizeMessage(
                'reaction.removeReact.ariaLabel',
                'remove reaction',
            )} ${emojiNameWithSpaces}`;
        }

        return (
            <OverlayTrigger
                delayShow={Constants.OVERLAY_TIME_DELAY}
                placement='top'
                shouldUpdatePosition={true}
                overlay={
                    <Tooltip className='reactionTooltip' id={`${this.props.post.id}-${this.props.emojiName}-reaction`}>
                        <ReactionTooltip
                            canAddReactions={canAddReactions}
                            canRemoveReactions={canRemoveReactions}
                            currentUserReacted={currentUserReacted}
                            emojiName={emojiName}
                            reactions={reactions}
                        />
                    </Tooltip>
                }
            >
                <button
                    id={`postReaction-${this.props.post.id}-${this.props.emojiName}`}
                    aria-label={ariaLabelEmoji}
                    className={`Reaction ${this.state.reactedClass} ${readOnlyClass}`}
                    onClick={this.handleClick}
                    onContextMenu={this.handleContextMenu}
                    onMouseEnter={this.loadMissingProfiles}
                    ref={this.reactionButtonRef}
                >
                    <span className='d-flex align-items-center'>
                        <span
                            className='Reaction__emoji emoticon'
                            style={{backgroundImage: 'url(' + this.props.emojiImageUrl + ')'}}
                        />
                        <span
                            ref={this.reactionCountRef}
                            className='Reaction__count'
                        >
                            <span className='Reaction__number'>
                                <span className='Reaction__number--display' data-testid='displayNumber'>{display}</span>
                                <span
                                    className='Reaction__number--unreacted'
                                    data-testid='unreacted'
                                    onAnimationEnd={this.handleAnimationEnded}
                                >
                                    {unreacted}
                                </span>
                                <span className='Reaction__number--reacted'>{reacted}</span>
                            </span>
                        </span>
                    </span>
                </button>
            </OverlayTrigger>
        );
    }
}
